import LocalMallOutlinedIcon from "@mui/icons-material/LocalMallOutlined";
import {
  Alert,
  Avatar,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Popover,
  Snackbar,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import { getFeatures } from "../../../config";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import useListNft from "../../../hooks/useListNft";
import { NFTIcon } from "../../../images";
import { RiNftFill } from "react-icons/ri";

import {
  assetContent,
  getAssets,
  getCollections,
} from "../../../store/reducers/asset";
import { AuthenticateState } from "../../../types/types";
import { encodeAssetUrl } from "../../../utils/format.utils";
import "./AssetModal.styles.css";
import { ActionButton } from "./WhiteBoardInput.styles";
import { getProticoClient } from "../../../utils/api";
const { FEATURE_NFT_LIST_ENABLED } = getFeatures();

interface Props {
  onSubmit?: (asset: assetContent) => void;
  submitNewMessage: any;
  setlistModalstatus?: any;
  setAsset?: any;
}

const AssetModal: React.FC<Props> = ({
  onSubmit,
  submitNewMessage,
  setlistModalstatus,
  setAsset,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentCollection, setCurrentCollection] = useState<string>("");
  const [currentRef, setCurrentRef] = useState<string>("");
  const [openAlert, setOpen] = React.useState(false);
  const { secondFontColor, brandColor } = useAppSelector(
    (state) => state.layout
  );
  const scrollCollectionContainerRef = useRef();
  const scrollAssetContainerRef = useRef();
  const {
    isFetchingCollection,
    collections: collectionList,
    assets: assetList,
    isFetchingAsset,
    collectionNextPageKey,
    assetnNextPageKey,
  } = useAppSelector((state) => state.asset);
  const { currentAddress, authenticateStatus } = useAppSelector(
    (state) => state.auth
  );
  const dispatch = useAppDispatch();
  const { checkERC721owner, checkERC1155owner, checkChainId } = useListNft();
  const collections = collectionList[currentAddress] ?? [];
  const assets = assetList[currentAddress] ?? {};
  const handleClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };
  const handleError = () => {
    setOpen(true);
  };

  useEffect(() => {
    if (
      authenticateStatus === AuthenticateState.PENDING ||
      authenticateStatus === AuthenticateState.GUEST
    )
      return;
    if (!currentAddress) return;
    dispatch(getCollections(undefined, collectionNextPageKey[currentAddress]));
  }, [authenticateStatus, currentAddress]);

  useEffect(() => {
    if (!collections.length || assets[collections[0].slug]) return;
    // setCurrentCollection(collections[0].slug);
  }, [collections]);

  useEffect(() => {
    if (currentCollection == "") return;
    if (assets[currentCollection]) return;
    dispatch(getAssets(currentCollection));
  }, [currentCollection]);

  useEffect(() => {
    if (!isOpen && !currentRef) return;
    setTimeout(() => document.getElementById(currentRef)?.scrollIntoView(), 0);
  }, [isOpen]);

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    setIsOpen((prev) => !prev);
  };

  const onAssetClick = (asset: assetContent) => {
    submitNewMessage(encodeAssetUrl(`${asset.imageUrl},${asset.permalink}`));
    // give rewards
    getProticoClient().engagement.recordNFTSentEvent();
  };
  const onListClick = async (asset: assetContent) => {
    checkListAvailable(asset);
  };

  const checkListAvailable = async (asset: assetContent) => {
    setlistModalstatus(true);
    setAsset(asset);
    setIsOpen(false);
  };

  const handleScrollCollection = () => {
    const container = scrollCollectionContainerRef.current as any;

    if (
      container.scrollLeft + container.clientWidth >=
      container.scrollWidth - 10
    ) {
      dispatch(
        getCollections(undefined, collectionNextPageKey[currentAddress])
      );
    }
  };

  const handleScrollAsset = () => {
    const container = scrollAssetContainerRef.current as any;
    if (
      container.scrollTop + container.clientHeight >=
      container.scrollHeight - 10
    ) {
      dispatch(getAssets(undefined, assetnNextPageKey[currentAddress]));
    }
  };

  const getAssetView = () => {
    if (isFetchingAsset)
      return (
        <div className="AssetModal-fetching">
          <CircularProgress style={{ color: brandColor }} size={20} />
        </div>
      );

    if (!assets[currentCollection] || !assets[currentCollection].length)
      return (
        <h3 className="AssetModal-fetching">
          {t("Asset_Modal_NoAsset") + " "}
          {!!collections.length && (
            <>
              <br />
              <br />
              {t("Asset_Modal_NoAsset_2")}
            </>
          )}
        </h3>
      );

    return (
      <div
        style={{ display: "flex", overflowY: "scroll" }}
        className="scroll-container"
        onScroll={handleScrollAsset}
        ref={scrollAssetContainerRef}
      >
        {assets[currentCollection]?.map((a, i) => (
          <div key={`asset_${i}`} style={{ position: "relative" }}>
            <Button
              size="small"
              type="button"
              className="AssetModal-asset"
              onClick={() => {
                if (a.imageUrl == NFTIcon) {
                  return;
                }
                if (!!onSubmit) onSubmit(a);
                else onAssetClick(a);
                setIsOpen(false);
              }}
            >
              <img src={a.imageUrl} className="AssetModal-asset-img" alt="" />
            </Button>
            {FEATURE_NFT_LIST_ENABLED && (
              <Tooltip
                title={t("List_Nft_List_Button")}
                placement="bottom-start"
                disableInteractive
                followCursor
                arrow
              >
                <IconButton
                  size="small"
                  color="primary"
                  style={{ position: "absolute", right: 0 }}
                  onClick={() => {
                    onListClick(a);
                  }}
                >
                  <LocalMallOutlinedIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ display: "flex" }}>
      <Tooltip title={t("FlashChat_Actions_Stickers")} placement="top-end" arrow disableInteractive>
        <label
          className="AssetModal-input-nft-action-button"
          onClick={handleClick}
        >
          <RiNftFill size={17}/>
        </label>
      </Tooltip>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: -13,
          horizontal: 10,
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        onClose={() => setIsOpen(false)}
      >
        <Snackbar open={openAlert} autoHideDuration={6000} onClose={handleClose}>
          <Alert onClose={handleClose} severity="error" sx={{ width: "100%" }}>
            List unavailable
          </Alert>
        </Snackbar>
        <Paper className="AssetModal-paper" sx={{ background: "#101010" }}>
          <SimpleBar className="AssetModal-asset-outer">
            {getAssetView()}
          </SimpleBar>
          <SimpleBar className="AssetModal-button-outer" scrollbarMinSize={50}>
            {isFetchingCollection && (
              <div className="AssetModal-fetching">
                <CircularProgress style={{ color: brandColor }} size={15} />
              </div>
            )}
            <div
              style={{ display: "flex", overflowX: "scroll" }}
              className="scroll-container"
              onScroll={handleScrollCollection}
              ref={scrollCollectionContainerRef}
            >
              {!isFetchingCollection &&
                collections.map((c, i) => (
                  <div key={`collection_${i}`} id={`collection_${i}`}>
                    <Tooltip title={c.name} arrow disableInteractive>
                      <Button
                        size="small"
                        className={`AssetModal-button ${
                          currentCollection === c.slug && "active"
                        }`}
                        type="button"
                        onClick={(e) => {
                          setCurrentCollection(c.slug);
                          setCurrentRef(`collection_${i}`);
                        }}
                      >
                        <Avatar
                          src={c.imageUrl}
                          style={{
                            width: "37px",
                            height: "37px",
                            margin: "2px",
                          }}
                        />
                      </Button>
                    </Tooltip>
                  </div>
                ))}
            </div>
          </SimpleBar>
        </Paper>
      </Popover>
    </div>
  );
};

export default AssetModal;
